import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import type { EventMountArg } from '@fullcalendar/core';
import type { SpaceWeeklySchedule } from '../models/Space';
import { CALENDAR_OVERLAY_COLORS } from '../styles/calendarOverlayStyles';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export interface CalendarOverlayEvent {
  id: string;
  title: string;
  /** Wall-clock local datetime in the calendar `timeZone` (no offset / Z). */
  start: string;
  end: string;
  backgroundColor?: string;
  textColor?: string;
  classNames?: string[];
  display?: 'background' | 'auto';
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateKeyFromZonedParts(zoned: Date): string {
  return `${zoned.getFullYear()}-${pad2(zoned.getMonth() + 1)}-${pad2(zoned.getDate())}`;
}

/** Monday→Sunday yyyy-MM-dd keys for the week containing `visibleStart`, in `timezone`. */
export function weekDateKeysInTimezone(visibleStart: Date, timezone: string): string[] {
  const zoned = utcToZonedTime(visibleStart, timezone);
  const day = zoned.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(zoned);
  monday.setDate(zoned.getDate() + mondayOffset);

  const keys: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const dayDate = new Date(monday);
    dayDate.setDate(monday.getDate() + i);
    keys.push(formatDateKeyFromZonedParts(dayDate));
  }
  return keys;
}

function dayScheduleForDateKey(
  weeklySchedule: SpaceWeeklySchedule,
  dateKey: string,
  timezone: string,
) {
  const noonUtc = zonedTimeToUtc(`${dateKey}T12:00:00`, timezone);
  const zoned = utcToZonedTime(noonUtc, timezone);
  return weeklySchedule[DAY_NAMES[zoned.getDay()]];
}

function normalizeHm(value: unknown): string | null {
  const minutes = parseHmToMinutes(value);
  if (minutes == null) return null;
  return formatMinutesAsSlotTime(minutes);
}

function wallClock(dateKey: string, hm: string): string {
  return `${dateKey}T${hm}`;
}

export function buildWorkingHoursOverlayEvents(
  weeklySchedule: SpaceWeeklySchedule | null | undefined,
  visibleStart: Date | null | undefined,
  timezone: string,
  _workingHoursColor = 'transparent',
): CalendarOverlayEvent[] {
  if (!weeklySchedule || !visibleStart || !timezone) return [];

  const events: CalendarOverlayEvent[] = [];
  const dateKeys = weekDateKeysInTimezone(visibleStart, timezone);
  const todayKey = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const nowWall = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd'T'HH:mm:ss");

  dateKeys.forEach((dateKey, i) => {
    const schedule = dayScheduleForDateKey(weeklySchedule, dateKey, timezone);
    if (!schedule || schedule.dayOff) return;
    if (dateKey < todayKey) return;

    if (schedule.openAllDay) {
      let start = wallClock(dateKey, '00:00:00');
      const end = wallClock(dateKey, '24:00:00');
      if (dateKey === todayKey && nowWall > start) {
        start = nowWall;
      }
      if (start >= end) return;
      events.push({
        id: `working-hours-${i}`,
        title: '',
        start,
        end,
        backgroundColor: CALENDAR_OVERLAY_COLORS.available,
        textColor: 'transparent',
        classNames: ['working-hours-indicator'],
        display: 'background',
      });
      return;
    }

    const startHm = normalizeHm(schedule.periodStart);
    const endHm = normalizeHm(schedule.periodEnd);
    if (!startHm || !endHm || startHm >= endHm) return;

    let start = wallClock(dateKey, startHm);
    const end = wallClock(dateKey, endHm);
    if (dateKey === todayKey && nowWall > start) {
      start = nowWall;
    }
    if (start >= end) return;

    events.push({
      id: `working-hours-${i}`,
      title: '',
      start,
      end,
      backgroundColor: CALENDAR_OVERLAY_COLORS.available,
      textColor: 'transparent',
      classNames: ['working-hours-indicator'],
      display: 'background',
    });
  });

  return events;
}

export function buildBlockedSlotOverlayEvents(
  weeklySchedule: SpaceWeeklySchedule | null | undefined,
  visibleStart: Date | null | undefined,
  timezone: string,
): CalendarOverlayEvent[] {
  if (!weeklySchedule || !visibleStart || !timezone) return [];

  const events: CalendarOverlayEvent[] = [];
  const dateKeys = weekDateKeysInTimezone(visibleStart, timezone);
  const todayKey = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');

  dateKeys.forEach((dateKey, i) => {
    if (dateKey < todayKey) return;

    const schedule = dayScheduleForDateKey(weeklySchedule, dateKey, timezone);
    if (!schedule || schedule.dayOff || schedule.openAllDay) return;

    const startHm = normalizeHm(schedule.periodStart);
    const endHm = normalizeHm(schedule.periodEnd);
    if (!startHm || !endHm || startHm >= endHm) return;

    const midnight = wallClock(dateKey, '00:00:00');
    const periodStart = wallClock(dateKey, startHm);
    const periodEnd = wallClock(dateKey, endHm);
    const dayEnd = wallClock(dateKey, '23:59:59');

    if (midnight < periodStart) {
      events.push({
        id: `blocked-before-${i}`,
        title: '',
        start: midnight,
        end: periodStart,
        backgroundColor: CALENDAR_OVERLAY_COLORS.closed,
        classNames: ['blocked-slot', 'closed-slot'],
        display: 'background',
      });
    }

    if (periodEnd < dayEnd) {
      events.push({
        id: `blocked-after-${i}`,
        title: '',
        start: periodEnd,
        end: dayEnd,
        backgroundColor: CALENDAR_OVERLAY_COLORS.closed,
        classNames: ['blocked-slot', 'closed-slot'],
        display: 'background',
      });
    }
  });

  return events;
}

export function buildDayOffOverlayEvents(
  weeklySchedule: SpaceWeeklySchedule | null | undefined,
  visibleStart: Date | null | undefined,
  timezone: string,
): CalendarOverlayEvent[] {
  if (!weeklySchedule || !visibleStart || !timezone) return [];

  const events: CalendarOverlayEvent[] = [];
  const dateKeys = weekDateKeysInTimezone(visibleStart, timezone);

  dateKeys.forEach((dateKey, i) => {
    const schedule = dayScheduleForDateKey(weeklySchedule, dateKey, timezone);
    if (!schedule?.dayOff) return;

    events.push({
      id: `blocked-day-off-${i}`,
      title: '',
      start: wallClock(dateKey, '00:00:00'),
      end: wallClock(dateKey, '23:59:59'),
      backgroundColor: CALENDAR_OVERLAY_COLORS.dayOff,
      classNames: ['blocked-slot', 'day-off'],
      display: 'background',
    });
  });

  return events;
}

/** True when a wall-clock end is at or before now in `timezone`. */
export function isCalendarWallClockPast(endWallClock: string, timezone: string): boolean {
  const nowWall = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd'T'HH:mm:ss");
  return endWallClock <= nowWall;
}

export function withPastEventClassNames(
  endWallClock: string,
  timezone: string,
  classNames: string[] = [],
): string[] {
  if (!isCalendarWallClockPast(endWallClock, timezone)) {
    return classNames;
  }
  return classNames.includes('past-event') ? classNames : [...classNames, 'past-event'];
}

/** Tag ended events after mount — FC timegrid columns don't get fc-day-past. */
export function mountPastEventClass(mountArg: EventMountArg, timezone: string): void {
  const display = mountArg.event.display;
  if (display === 'background' || display === 'inverse-background') {
    return;
  }
  const end = mountArg.event.end;
  if (!end || !timezone) {
    return;
  }
  const endWall = formatInTimeZone(end, timezone, "yyyy-MM-dd'T'HH:mm:ss");
  if (isCalendarWallClockPast(endWall, timezone)) {
    mountArg.el.classList.add('past-event');
  }
}

export function buildPastBlockedOverlayEvents(
  visibleStart: Date | null | undefined,
  timezone: string,
): CalendarOverlayEvent[] {
  if (!visibleStart || !timezone) return [];

  const events: CalendarOverlayEvent[] = [];
  const dateKeys = weekDateKeysInTimezone(visibleStart, timezone);
  const todayKey = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const nowWall = formatInTimeZone(new Date(), timezone, "yyyy-MM-dd'T'HH:mm:ss");

  dateKeys.forEach((dateKey, i) => {
    if (dateKey === todayKey) {
      const start = wallClock(dateKey, '00:00:00');
      if (start < nowWall) {
        events.push({
          id: `blocked-past-today-${i}`,
          title: '',
          start,
          end: nowWall,
          backgroundColor: CALENDAR_OVERLAY_COLORS.past,
          classNames: ['blocked-slot', 'past-slot'],
          display: 'background',
        });
      }
    } else if (dateKey < todayKey) {
      events.push({
        id: `blocked-past-${i}`,
        title: '',
        start: wallClock(dateKey, '00:00:00'),
        end: wallClock(dateKey, '23:59:59'),
        backgroundColor: CALENDAR_OVERLAY_COLORS.past,
        classNames: ['blocked-slot', 'past-slot'],
        display: 'background',
      });
    }
  });

  return events;
}

function parseHmToMinutes(value: unknown): number | null {
  if (value == null) return null;

  // Jackson sometimes serializes LocalTime as [hour, minute] or [hour, minute, second].
  if (Array.isArray(value) && value.length >= 2) {
    const hours = Number(value[0]);
    const minutes = Number(value[1]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;
    return Math.min(24 * 60, hours * 60 + minutes);
  }

  if (typeof value === 'object') {
    const record = value as { hour?: unknown; minute?: unknown };
    if (record.hour != null && record.minute != null) {
      return parseHmToMinutes([record.hour, record.minute]);
    }
  }

  if (typeof value !== 'string' || !value.trim()) return null;
  const [hourStr, minuteStr] = value.trim().split(':');
  const hours = Number(hourStr);
  const minutes = Number(minuteStr ?? 0);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;
  return Math.min(24 * 60, hours * 60 + minutes);
}

function formatMinutesAsSlotTime(totalMinutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, totalMinutes));
  const hours = Math.floor(clamped / 60);
  const mins = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

/**
 * Visible FullCalendar axis for a weekly schedule: earliest open → latest close.
 * Timed days drive the axis. A single "open 24h" day does not expand the week to 00–24;
 * only when every open day is 24h do we show a full-day axis.
 */
export function calculateScheduleTimeRange(
  weeklySchedule: SpaceWeeklySchedule | null | undefined,
  options?: { paddingMinutes?: number },
): {
  slotMinTime: string;
  slotMaxTime: string;
} {
  const paddingMinutes = Math.max(0, options?.paddingMinutes ?? 0);

  if (!weeklySchedule) {
    return { slotMinTime: '08:00:00', slotMaxTime: '22:00:00' };
  }

  let earliestStart = 24 * 60;
  let latestEnd = 0;
  let timedDayCount = 0;
  let openAllDayCount = 0;

  for (const dayName of DAY_NAMES) {
    const schedule = weeklySchedule[dayName];
    if (!schedule || schedule.dayOff) continue;

    if (schedule.openAllDay) {
      openAllDayCount += 1;
      continue;
    }

    const startMinutes = parseHmToMinutes(schedule.periodStart);
    const endMinutes = parseHmToMinutes(schedule.periodEnd);
    if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
      continue;
    }

    timedDayCount += 1;
    earliestStart = Math.min(earliestStart, startMinutes);
    latestEnd = Math.max(latestEnd, endMinutes);
  }

  if (timedDayCount === 0) {
    if (openAllDayCount > 0) {
      return { slotMinTime: '00:00:00', slotMaxTime: '24:00:00' };
    }
    return { slotMinTime: '08:00:00', slotMaxTime: '22:00:00' };
  }

  const paddedStart = Math.max(0, earliestStart - paddingMinutes);
  const paddedEnd = Math.min(24 * 60, latestEnd + paddingMinutes);

  return {
    slotMinTime: formatMinutesAsSlotTime(paddedStart),
    slotMaxTime: formatMinutesAsSlotTime(paddedEnd),
  };
}
